/**
 * Footer accordion: toggles menu visibility on mobile when the trigger is clicked.
 * On desktop (min-width: 990px) the accordion is inactive; on resize to desktop
 * any open state is cleared. Uses matchMedia so behavior is disabled outside
 * the mobile breakpoint.
 */
if (!customElements.get('footer-accordion')) {
  class FooterAccordion extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.mediaQuery = window.matchMedia('(max-width: 989px)');

      const trigger = this.querySelector('[data-accordion-trigger]');
      if (trigger) {
        if (this.mediaQuery.matches) {
          trigger.setAttribute('aria-expanded', 'false');
        }
        trigger.addEventListener('click', this.handleTriggerClick.bind(this), { signal });
      }

      this.mediaQuery.addEventListener('change', this.handleMediaChange.bind(this), { signal });
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    /**
     * Handles trigger click; no-op when not in mobile breakpoint.
     */
    handleTriggerClick() {
      if (!this.mediaQuery.matches) return;
      this.toggle();
    }

    /**
     * Handles media query change; closes accordion when switching to desktop.
     * @param {MediaQueryListEvent} e
     */
    handleMediaChange(e) {
      if (!e.matches) {
        this.close();
      }
    }

    /**
     * Toggles accordion open/closed state.
     */
    toggle() {
      const isOpen = this.hasAttribute('data-open');
      if (isOpen) {
        this.close();
      } else {
        this.open();
      }
    }

    /**
     * Opens the accordion: sets data-open and aria-expanded="true".
     */
    open() {
      this.setAttribute('data-open', '');
      const trigger = this.querySelector('[data-accordion-trigger]');
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'true');
      }
    }

    /**
     * Closes the accordion: removes data-open and sets aria-expanded="false".
     */
    close() {
      this.removeAttribute('data-open');
      const trigger = this.querySelector('[data-accordion-trigger]');
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
      }
    }
  }

  customElements.define('footer-accordion', FooterAccordion);
}
